﻿import { ServiceDeskConfig } from "../../ServiceDesk/Startup/serviceDeskConfig";
import { SectionIdDefaultValue } from "../../ServiceDesk/preference/preferenceController";
import { Prerequisite } from "../../ServiceDesk/sdServices/loading/prerequisite-loading-service";
import { LoggingService } from "../../ServiceDesk/sdServices/loggingSvc";
import { PreferenceModel, PreferenceService } from "../../ServiceDesk/sdServices/preferenceSvc";
import { v4 } from "uuid";
import { MsalAuthenticationServiceImplementation } from "../../ServiceDesk/sdServices/msal/msal-authentication-service";

/**
 * DEPRECATED
 * Exposes localization method to client code
 */
export interface LocalizationApi {
  buildUrl(subAppId: string, locale: string): string;
  initLocalizedResources(subAppId: string): ng.IPromise<any>;
  getLocalizedString(subAppId: string, value: string): string;
  doesKeyExist(subAppId: string, value: string): boolean;
}

type LocArray = { [key: string]: any };
const DEFAULT_LOCALE = "en-us";
export const LANGUAGE_PREFERENCE_APP_ID = "ServiceDesk";
export const LANGUAGE_PREFERENCE_KEY = "language";
/*
 * An AngularJS Localization Service
 *
 */
export class LocalizationService implements LocalizationApi {
  private locale: string;
  private dictionary: LocArray;
  private appLoadedDictionary: {
    [appId: string]: { promise: ng.IPromise<any>; isEmpty: boolean; deferred: ng.IDeferred<any> };
  } = {};
  private guaranteedSubAppIds = ["servicedesk", "shellcommon", "documentation"];
  private cacheBustParameter = v4();
  private hasLoggedLocFileMissingError = false;
  private loggedMissingLocKeys: string[] = [];

  // @ngInject
  constructor(
    private $http: ng.IHttpService,
    private $q: ng.IQService,
    private loggingService: LoggingService,
    private preferenceService: PreferenceService,
    private msalAuthenticationService: MsalAuthenticationServiceImplementation,
    private sdConfig: ServiceDeskConfig
  ) {
    // For now, we are forcing the code to use en-US. We could use "windowService.navigator.userLanguage || windowService.navigator.language" to
    // detect the browser locale but since we have no plan to localize the app yet, it is best to avoid any edge case scenario
    // Also, we may want to use an "in site" setting, saved in the profile to provide language selection instead.
    this.locale = DEFAULT_LOCALE;
    this.dictionary = {};
  }

  public buildUrl(subAppId: string, locale: string): string {
    locale = this.coalesceOldLocaleValues(locale);
    let locJsonPrefix = subAppId + "/Loc/";
    let revisions = this.sdConfig.manifestRevisions;
    if (revisions) {
      let subApp = revisions[Object.keys(revisions).find((value) => value.toLowerCase() == subAppId.toLowerCase())];
      if (subApp) {
        let beaconBaseUrl: string;
        let beacons = this.sdConfig.absolutePathBeacons;
        let packageBaseLocation = beacons[subAppId.toLowerCase()];
        if (packageBaseLocation) {
          beaconBaseUrl = packageBaseLocation.substr(0, packageBaseLocation.length - subAppId.length);
        }

        let loc = subApp[Object.keys(subApp).find((value) => value.toLowerCase() == "Loc".toLowerCase())];
        if (loc) {
          let revisedLocaleJson = loc[Object.keys(loc).find((value) => value.toLowerCase() == locale.toLowerCase())];
          return (beaconBaseUrl || revisions._rootPath || "../") + locJsonPrefix + revisedLocaleJson;
        }
      }
    }

    return `../${locJsonPrefix}${locale}.json?cacheBust=${this.cacheBustParameter}`;
  }

  public initLocalizedResources(subAppId: string): ng.IPromise<any> {
    let savedPromise = this.appLoadedDictionary[subAppId];
    if (savedPromise && !savedPromise.isEmpty) {
      return savedPromise.promise;
    }
    let defaultLocaleUrl = this.buildUrl(subAppId, DEFAULT_LOCALE);
    let defaultLocalePromise = this.loadLocaleFile(defaultLocaleUrl);
    let preferencePromise = this.msalAuthenticationService.userInfo.isAuthenticated
      ? this.preferenceService.getPreference(LANGUAGE_PREFERENCE_APP_ID)
      : this.$q.resolve(undefined);
    let preferenceCheck = preferencePromise
      .then((preference) => {
        if (preference && preference.content[LANGUAGE_PREFERENCE_KEY]) {
          this.locale = preference.content[LANGUAGE_PREFERENCE_KEY];
        }
        let url = this.buildUrl(subAppId, this.locale);
        if (this.locale && this.locale.toLowerCase() != DEFAULT_LOCALE && !this.dictionary[url]) {
          return this.loadLocaleFile(url);
        }
      })
      .catch((error) => {
        if (this.locale == "ps-ps") {
          return this.$q.resolve();
        }
        return error;
      });
    let promise = this.$q.all([preferenceCheck, defaultLocalePromise]);
    if (savedPromise) {
      promise
        .then((result) => {
          savedPromise.deferred.resolve(result);
        })
        .catch((error) => {
          savedPromise.deferred.reject(error);
        });
      savedPromise.isEmpty = false;
    } else {
      this.appLoadedDictionary[subAppId] = { promise: promise, isEmpty: false, deferred: undefined };
    }
    return promise;
  }

  private coalesceOldLocaleValues(locale: string) {
    switch (locale.toLowerCase()) {
      case "ja":
        return "ja-jp";
      case "pt":
        return "pt-br";
      case "es":
        return "es-es";
    }

    return locale;
  }

  private loadLocaleFile(url: string) {
    return this.$http.get(url).then(
      (response) => {
        let locFile: any = response.data;
        if (this.dictionary) {
          let lowercasedLocFile: any = {};
          Object.keys(locFile).reduce(
            (lowercasedLocFile, key) => ((lowercasedLocFile[key.toLowerCase()] = locFile[key]), lowercasedLocFile),
            lowercasedLocFile
          );
          this.dictionary[url] = lowercasedLocFile;
        }
      },
      (error) => {
        this.loggingService.logError(`failed to download localization file ${url}`);
      }
    );
  }

  public getLocale(): string {
    return this.locale;
  }

  public setLocale(locale: string): ng.IPromise<any> {
    let preferenceModel: PreferenceModel = {
      appId: LANGUAGE_PREFERENCE_APP_ID,
      sectionId: SectionIdDefaultValue,
      content: { [LANGUAGE_PREFERENCE_KEY]: <any>locale }
    };
    return this.preferenceService.setPreference(preferenceModel);
  }

  public getLocalizedString(subAppId: string, value: string): string {
    return this.getLocalizedStringInternal(subAppId, value, undefined);
  }

  public doesKeyExist(subAppId: string, value: string): boolean {
    return this.doesKeyExistInternal(subAppId, value, undefined);
  }

  public whenReady(subAppId: string): ng.IPromise<any> {
    if (this.appLoadedDictionary[subAppId]) {
      return this.appLoadedDictionary[subAppId].promise;
    } else {
      let deferred = this.$q.defer();
      this.appLoadedDictionary[subAppId] = { promise: deferred.promise, deferred: deferred, isEmpty: true };
      return deferred.promise;
    }
  }
  public prerequisiteLoadCallback(prerequisite: Prerequisite): ng.IPromise<any> {
    if (!prerequisite.localizations) {
      return this.$q.resolve();
    }
    if (
      !angular.isArray(prerequisite.localizations) ||
      prerequisite.localizations.find((locale: string) => {
        return typeof locale != "string";
      })
    ) {
      throw new Error(`Prerequisite contains field localization which is malformed. ${JSON.stringify(prerequisite)}`);
    }
    let appIds = Array.from(new Set<string>(prerequisite.localizations));
    let allPromises = appIds.map((appId) => {
      return this.initLocalizedResources(appId);
    });
    return this.$q.all(allPromises);
  }

  private getLocalizedStringInternal(subAppId: string, value: string, localeParam: string): string {
    let locale = localeParam ? localeParam : this.locale;

    // build the url to retrieve the localized resource file
    let url = this.buildUrl(subAppId, locale);
    let locFile: any;
    if (this.dictionary) {
      locFile = this.dictionary[url];
    }

    // check to see if the resource file has been loaded
    if (!locFile) {
      if (!this.isGuaranteedSubApp(subAppId)) {
        // do not log multiple times if a locale file is missing
        if (!this.hasLoggedLocFileMissingError) {
          this.hasLoggedLocFileMissingError = true;
          this.loggingService.logError(`localization file not loaded ${url} for ${locale}. locale`);
        }
      } else {
        // Guaranteed sub apps may see incidental uses of this method while the app is loading, we don't consider this an error case.
        //  If we ever introduce verbose messaging we would consider logging this at some point.
      }

      return this.getBackupLocalizedString(subAppId, value, locale);
    }

    if (locFile[value.toLowerCase()]) {
      return locFile[value.toLowerCase()];
    }

    if (this.loggedMissingLocKeys.indexOf(value) < 0) {
      this.loggedMissingLocKeys.push(value);
      this.loggingService.logError(`localization missing for key: ${value} and locale: ${this.locale}`);
    }

    return this.getBackupLocalizedString(subAppId, value, locale); // maybe in the future return the english version instead of the key.
  }

  private getBackupLocalizedString(subAppId: string, value: string, locale: string): string {
    if (locale != DEFAULT_LOCALE) {
      return this.getLocalizedStringInternal(subAppId, value, DEFAULT_LOCALE);
    }
    return value;
  }

  private doesKeyExistInternal(subAppId: string, value: string, localeParam: string): boolean {
    let locale = localeParam ? localeParam : this.locale;
    let url = this.buildUrl(subAppId, locale);
    let locFile: LocArray;
    if (this.dictionary) {
      locFile = this.dictionary[url];
    }
    // The key is existing
    if (locFile && locFile[value.toLowerCase()]) {
      return true;
    }
    // Log error if
    //  1. resouce file isn't loaded
    //  2. key is missing in backup locale
    if (!locFile && !this.isGuaranteedSubApp(subAppId)) {
      this.loggingService.logError(`localization file not loaded ${url}.`);
    } else if (locale == DEFAULT_LOCALE) {
      this.loggingService.logError(`localization missing for key: ${value} and locale: ${this.locale}`);
    }
    // Check backup resouce
    if (locale != DEFAULT_LOCALE) {
      return this.doesKeyExistInternal(subAppId, value, DEFAULT_LOCALE);
    }
    return false;
  }

  // Any sub applications which are guaranteed to be loaded by SD's initialization code fall under this category.
  //  This distinction is used to suppress specific incidental localization issues that should still apply for all non-
  //  guaranteed sub applications.
  private isGuaranteedSubApp(subAppId: string) {
    return this.guaranteedSubAppIds.includes(subAppId.toLowerCase());
  }
}
